import React from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DollarFormatter, EmployeeFormatter } from "../../../helper/formatting";
import {
  ESTIMATED_LABEL,
  ESTIMATED_TOOLTIP,
  NOT_REPORTED,
  formatHeadcountBucket,
  formatRevenueBucket,
  isEstimatedMetric,
} from "../../../helper/metricBuckets";
import { CompanyPropTypes } from "../../../schema/company";
import { isMetricRenderable } from "./metricRangeDomain";
import SummaryMetricCard from "./SummaryMetricCard";
import MetricRangeChart from "./MetricRangeChart";
import HeadcountCompareChart from "./HeadcountCompareChart";
import CompanySidebarEmptyState from "./CompanySidebarEmptyState";
import { SIDEBAR } from "./sidebarVisual";
import { SidebarCard, SidebarSectionTitle } from "./SidebarPrimitives";

function formatMetricValue(metric, formatter) {
  if (!isMetricRenderable(metric?.pointEstimate, metric?.upperBound)) return "N/A";
  return formatter.format(metric.pointEstimate);
}

function formatMetricDetail(metric, formatter) {
  if (!isMetricRenderable(metric?.pointEstimate, metric?.upperBound)) return null;
  if (typeof metric.lowerBound !== "number" || typeof metric.upperBound !== "number") return null;
  return `${formatter.format(metric.lowerBound)} – ${formatter.format(metric.upperBound)}`;
}

const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);

// Revenue/headcount headlines use the bucket text (owner ruling Q1, FE #650), which already
// carries the range, so the cards drop the duplicate range detail. The range chart plots a
// point estimate, so it only draws for legacy rows that have a finite point and both bounds.
const hasPointAndRange = (metric) =>
  isFiniteNumber(metric?.pointEstimate) &&
  isFiniteNumber(metric?.lowerBound) &&
  isFiniteNumber(metric?.upperBound);

/**
 * Pin-style Recharts metrics panel for V2 company surfaces.
 * Honest to AlphaSearch estimate/range fields — no invented time series.
 */
export default function CompanyMetricsPanelV2({
  companyDetail,
  showTitle = true,
  compact = false,
  summaryCardMuted = false,
}) {
  const theme = useTheme();
  const accentColor = theme.palette.primary.main;
  const rangeHeight = compact
    ? SIDEBAR.metrics.compactRangeHeight
    : SIDEBAR.metrics.rangeHeight;
  const revenueHeight = compact
    ? SIDEBAR.metrics.compactRevenueHeight
    : SIDEBAR.metrics.revenueHeight;
  const compareHeight = compact
    ? SIDEBAR.metrics.compactCompareHeight
    : SIDEBAR.metrics.compareHeight;
  const chartCardSx = { py: SIDEBAR.metrics.chartPadY };

  const qualityScore =
    typeof companyDetail?.qualityScore === "number" && Number.isFinite(companyDetail.qualityScore)
      ? companyDetail.qualityScore
      : null;

  const revenueLabel = formatRevenueBucket(companyDetail?.revenue);
  const headcountLabel = formatHeadcountBucket(companyDetail?.harmonizedHeadcount);
  const headcountEstimated = Boolean(headcountLabel) && isEstimatedMetric(companyDetail?.harmonizedHeadcount);
  const hasHeadcountRange = hasPointAndRange(companyDetail?.harmonizedHeadcount);
  const hasRevenueRange = hasPointAndRange(companyDetail?.revenue);
  const hasBreakdown = [companyDetail?.totalHeadcount, companyDetail?.fteHeadcount, companyDetail?.onlineHeadcount]
    .some((metric) => typeof metric?.pointEstimate === "number" && Number.isFinite(metric.pointEstimate));
  const hasRevenuePerEmployee = isMetricRenderable(
    companyDetail?.revenuePerEmployee?.pointEstimate,
    companyDetail?.revenuePerEmployee?.upperBound
  );

  const hasAnyMetric =
    headcountLabel != null ||
    revenueLabel != null ||
    hasBreakdown ||
    qualityScore != null ||
    hasRevenuePerEmployee;

  if (!hasAnyMetric) {
    return (
      <Box
        data-testid="company-metrics-panel-v2"
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        {showTitle ? <SidebarSectionTitle>Company Metrics</SidebarSectionTitle> : null}
        <CompanySidebarEmptyState variant="metrics" testId="metrics-tab-empty-state" />
      </Box>
    );
  }

  return (
    <Box data-testid="company-metrics-panel-v2">
      {showTitle ? <SidebarSectionTitle>Company Metrics</SidebarSectionTitle> : null}

      <Box sx={{ display: "flex", flexDirection: "column", gap: SIDEBAR.gap.card }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: compact
              ? "repeat(2, minmax(0, 1fr))"
              : {
                  xs: "1fr",
                  sm: hasRevenuePerEmployee
                    ? "repeat(4, minmax(0, 1fr))"
                    : "repeat(3, minmax(0, 1fr))",
                },
            gap: SIDEBAR.gap.card,
          }}
        >
          <SummaryMetricCard
            label="Revenue"
            value={revenueLabel ?? NOT_REPORTED}
            detail={null}
            mutedSurface={summaryCardMuted}
          />
          <SummaryMetricCard
            label="Headcount"
            value={headcountEstimated ? `${headcountLabel} ${ESTIMATED_LABEL}` : (headcountLabel ?? NOT_REPORTED)}
            detail={headcountEstimated ? ESTIMATED_TOOLTIP : null}
            mutedSurface={summaryCardMuted}
          />
          <Box sx={compact && !hasRevenuePerEmployee ? { gridColumn: "1 / -1" } : undefined}>
            <SummaryMetricCard
              label="Info quality"
              value={qualityScore == null ? "N/A" : `${qualityScore}/42`}
              detail={qualityScore == null ? null : "Completeness score"}
              mutedSurface={summaryCardMuted}
            />
          </Box>
          {hasRevenuePerEmployee ? (
            <SummaryMetricCard
              label="Revenue per employee"
              value={formatMetricValue(companyDetail.revenuePerEmployee, DollarFormatter)}
              detail={formatMetricDetail(companyDetail.revenuePerEmployee, DollarFormatter)}
              mutedSurface={summaryCardMuted}
            />
          ) : null}
        </Box>

        {hasBreakdown ? (
          <SidebarCard sx={chartCardSx}>
            <HeadcountCompareChart
              totalHeadcount={companyDetail?.totalHeadcount}
              fteHeadcount={companyDetail?.fteHeadcount}
              onlineHeadcount={companyDetail?.onlineHeadcount}
              accentColor={accentColor}
              height={compareHeight}
              compact={compact}
            />
          </SidebarCard>
        ) : null}

        {(hasHeadcountRange || hasRevenueRange) ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: compact || !(hasHeadcountRange && hasRevenueRange) ? "1fr" : "1fr 1fr",
              },
              gap: SIDEBAR.gap.card,
            }}
          >
            {hasHeadcountRange ? (
              <SidebarCard sx={chartCardSx}>
                <MetricRangeChart
                  title="Headcount estimate range"
                  metricLabel="Headcount"
                  pointEstimate={companyDetail?.harmonizedHeadcount?.pointEstimate}
                  lowerBound={companyDetail?.harmonizedHeadcount?.lowerBound}
                  upperBound={companyDetail?.harmonizedHeadcount?.upperBound}
                  dataPoints={companyDetail?.harmonizedHeadcount?.dataPoints}
                  formatter={EmployeeFormatter}
                  accentColor={accentColor}
                  height={rangeHeight}
                  compact={compact}
                />
              </SidebarCard>
            ) : null}
            {hasRevenueRange ? (
              <SidebarCard sx={chartCardSx}>
                <MetricRangeChart
                  title="Revenue estimate range"
                  metricLabel="Revenue"
                  pointEstimate={companyDetail?.revenue?.pointEstimate}
                  lowerBound={companyDetail?.revenue?.lowerBound}
                  upperBound={companyDetail?.revenue?.upperBound}
                  dataPoints={companyDetail?.revenue?.dataPoints}
                  formatter={DollarFormatter}
                  accentColor={accentColor}
                  height={revenueHeight}
                  compact={compact}
                />
              </SidebarCard>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

CompanyMetricsPanelV2.propTypes = {
  companyDetail: CompanyPropTypes,
  showTitle: PropTypes.bool,
  compact: PropTypes.bool,
  summaryCardMuted: PropTypes.bool,
};
